import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AddressRequestDTO, CalculateTripRequestDTO, DistanceResponseDTO, AccountDTO } from '../../interfaces/models';
import { icon, Marker, divIcon, marker, MarkerOptions } from 'leaflet';
import * as dayjs from 'dayjs';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import { CityAndState } from '../../interfaces/cityAndStateResponse';
import { EncodeHelper } from '../../helpers/encode.helper';

dayjs.extend(relativeTime);

export interface HereGeocodeResult {
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  label?: string;
  lat?: string;
  lon?: string;
  houseNumber?: string;
  street?: string;
  countryCode?: string;
}

export interface HereRoute {
  shape?: string[];
}

const QUERY = '&mode=fastest;truck;traffic:disabled&&height=1&routeattributes=sh,bb,gr';
const QUERY_REVERSE = '&mode=retrieveAll';

@Injectable({ providedIn: 'root' })

export class HereService {
  constructor(
    protected httpClient: HttpClient,
  ) { }

  public getGeocode(search: string): Observable<HereGeocodeResult[]> {
    return this.httpClient.get<HereGeocodeResult[]>(
      `${environment.api}/here/geocode?maxresults=5&searchtext=${encodeURIComponent((search || '').trim())}`,
    );
  }

  public getReverseGeocode(search: string): Observable<HereGeocodeResult[]> {
    return this.httpClient.get<HereGeocodeResult[]>(`${environment.api}/here/reversegeocode?maxresults=1&prox=${search},250${QUERY_REVERSE}`);
  }

  public getRoute(point0: string, point1: string): Observable<HereRoute[]> {
    return this.httpClient.get<HereRoute[]>(`${environment.api}/here/calculateroute?waypoint0=${point0}&waypoint1=${point1}${QUERY}`);
  }

  public validateAddress(body: AddressRequestDTO): Observable<unknown> {
    return this.httpClient.post<unknown>(`${environment.api}/here/validate-address`, { ...body, country: 'United States' });
  }

  public calculateRoute(data: CalculateTripRequestDTO): Observable<DistanceResponseDTO> {
    return this.httpClient.post<DistanceResponseDTO>(`${environment.api}/here/calculate-trip`, data);
  }

  public getRouteTrip(query: Record<string, string | number | boolean>): Observable<HereRoute[]> {
    const filter = EncodeHelper.encodeData(query);
    return this.httpClient.get<HereRoute[]>(`${environment.api}/here/calculateroute?${filter}`);
  }

  public setIconsLeaflet() {
    const iconRetinaUrl = 'assets/marker-icon-2x.png';
    const iconUrl = 'assets/marker-icon.png';
    const shadowUrl = 'assets/marker-shadow.png';
    const iconDefault = icon({
      iconRetinaUrl,
      iconUrl,
      shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41],
    });
    Marker.prototype.options.icon = iconDefault;
  }

  public getPlannerMarkerIcon(className: string) {

    const iconResult = divIcon({
      className: '',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41],
      html: `<span class='marker ${className}'"><span class='marker_circle'></span></span>`,
    });

    return iconResult;
  }

  public getAccountMarker(account: AccountDTO, markerOptions: MarkerOptions) {
    const { locations } = account;
    if (locations && locations.length) {
      const [location] = locations;
      const { lat, lon, createdAt } = location;

      const avatar = account.avatarUrl || 'assets/image/avatar-default.jpg';
      const popup = `
      <div class="flex flex--row marker_block" id="driver_${account.id}">
          <img class="marker_block--img" src="${avatar}">
          <span>
            <div>${account.firstName} ${account.lastName}</div>
            <div>${dayjs(createdAt).fromNow()}</div>
          </span>
      </div>`;

      return marker(
        [lat, lon],
        markerOptions,
      )
        .bindPopup(popup)
        .on('mouseover', (ev) => {
          ev.target.openPopup();
        });
    }

    return null;
  }

  public getCitiesAndStates(query: { city: string }) {
    const filter = EncodeHelper.encodeData(query);
    return this.httpClient.get<CityAndState[]>(`${environment.api}/here/cities-and-states?${filter}`);
  }
}
